import { useState } from 'react';
import { Select } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useBulkSetFlag, ProductFlagType } from '@modules/products';
import { useCustomSets, useAddProductsToCustomSet } from '@modules/custom-sets';
import { Button, Modal } from '@shared/ui';

interface AddToSetModalProps {
    isOpen: boolean;
    onClose: () => void;
    productIds: number[];
    onSuccess?: () => void;
}

export default function AddToSetModal({
    isOpen,
    onClose,
    productIds,
    onSuccess,
}: AddToSetModalProps) {
    const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

    const bulkSetPopular = useBulkSetFlag(ProductFlagType.POPULAR);
    const bulkSetNew = useBulkSetFlag(ProductFlagType.NEW);
    const bulkSetHidden = useBulkSetFlag(ProductFlagType.HIDDEN);
    const bulkSetOutOfStock = useBulkSetFlag(ProductFlagType.OUT_OF_STOCK);
    const addProductsToCustomSet = useAddProductsToCustomSet();
    const { data: customSets = [] } = useCustomSets();

    const handleAddToSet = async () => {
        if (!selectedSetId || productIds.length === 0) {
            return;
        }

        try {
            // Обрабатываем специальные подборки
            if (selectedSetId === 'popular') {
                await bulkSetPopular.mutateAsync({
                    productIds,
                    value: true,
                });
            } else if (selectedSetId === 'new') {
                await bulkSetNew.mutateAsync({
                    productIds,
                    value: true,
                });
            } else if (selectedSetId === 'hidden') {
                await bulkSetHidden.mutateAsync({
                    productIds,
                    value: true,
                });
            } else if (selectedSetId === 'out-of-stock') {
                await bulkSetOutOfStock.mutateAsync({
                    productIds,
                    value: true,
                });
            } else {
                // Обычная кастомная подборка
                const setId = parseInt(selectedSetId, 10);
                await addProductsToCustomSet.mutateAsync({
                    id: setId,
                    productIds,
                });
            }

            // Показываем уведомление об успехе
            notifications.show({
                title: 'Успешно',
                message: 'Товары успешно добавлены в подборку',
                color: 'green',
            });

            // Закрываем модальное окно и сбрасываем выбор
            setSelectedSetId(null);
            onClose();
            onSuccess?.();
        } catch (error) {
            console.error('Ошибка при добавлении товаров в подборку:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при добавлении товаров в подборку',
                color: 'red',
            });
        }
    };

    const handleClose = () => {
        setSelectedSetId(null);
        onClose();
    };

    const isLoading = addProductsToCustomSet.isPending || bulkSetPopular.isPending || bulkSetNew.isPending || bulkSetHidden.isPending || bulkSetOutOfStock.isPending;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size={400}
            title="Добавить товары в подборку"
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Select
                    placeholder="Выберите подборку"
                    value={selectedSetId}
                    onChange={(value) => setSelectedSetId(value)}
                    data={[
                        { value: 'popular', label: '🔥 Популярное' },
                        { value: 'new', label: '✨ Новинки' },
                        { value: 'hidden', label: '🔒 Скрытые' },
                        { value: 'out-of-stock', label: '❌ Закончились' },
                        ...customSets.map(set => ({
                            value: set.id.toString(),
                            label: set.name,
                        })),
                    ]}
                    style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button onClick={handleClose} variant="secondary">
                        Отмена
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddToSet}
                        disabled={!selectedSetId || isLoading}
                        loading={isLoading}
                    >
                        Добавить
                    </Button>
                </div>
            </div>
        </Modal>
    );
}